import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/assets/icons/LibrarySvg';
import { tileButton } from '@/components/Board/commonToolbarStyles';
import { NumberInput } from '@/components/Board/NumberInput';
import Tooltip from '@/components/Common/Tooltip';
const GRID_CONSTANTS = {
    DEFAULT_COLOR: '#cccccc',
    DEFAULT_SIZE: 30,
    MIN_SIZE: 5,
    MAX_SIZE: 200,
    DEBOUNCE_DELAY: 100,
};
const STEP_MODES = {
    FIXED: '1',
    FLOATING_1: '0.1',
    FLOATING_2: '0.01',
};
/**
 * Компонент для управления настройками локальной сетки
 */
export const GridSettings = ({ element, onLocalGridChange, variant = 'default', }) => {
    var _a, _b, _c;
    const [localColor, setLocalColor] = useState(GRID_CONSTANTS.DEFAULT_COLOR);
    const [fixedStep, setFixedStep] = useState(true);
    const debounceRef = useRef(null);
    const currentGrid = (_a = element.props) === null || _a === void 0 ? void 0 : _a.localGrid;
    // Синхронизация localColor с текущим элементом
    useEffect(() => {
        var _a;
        setLocalColor((_a = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.color) !== null && _a !== void 0 ? _a : GRID_CONSTANTS.DEFAULT_COLOR);
    }, [currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.color]);
    // Debounced функция для обновления сетки
    const debouncedOnLocalGridChange = useCallback((newLocalGrid) => {
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onLocalGridChange(newLocalGrid);
        }, GRID_CONSTANTS.DEBOUNCE_DELAY);
    }, [onLocalGridChange]);
    const handleEnabledChange = useCallback((enabled) => {
        var _a, _b;
        onLocalGridChange({
            enabled,
            size: (_a = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.size) !== null && _a !== void 0 ? _a : GRID_CONSTANTS.DEFAULT_SIZE,
            color: (_b = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.color) !== null && _b !== void 0 ? _b : GRID_CONSTANTS.DEFAULT_COLOR,
        });
    }, [onLocalGridChange, currentGrid]);
    const handleSizeChange = useCallback((value) => {
        var _a, _b;
        const finalValue = fixedStep ? Math.round(value) : value;
        onLocalGridChange({
            enabled: (_a = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.enabled) !== null && _a !== void 0 ? _a : false,
            size: finalValue,
            color: (_b = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.color) !== null && _b !== void 0 ? _b : GRID_CONSTANTS.DEFAULT_COLOR,
        });
    }, [onLocalGridChange, currentGrid, fixedStep]);
    const handleFixedStepChange = useCallback((checked) => {
        var _a, _b, _c;
        setFixedStep(checked);
        // Если включаем фиксированный шаг, округляем текущее значение
        if (checked) {
            const currentSize = (_a = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.size) !== null && _a !== void 0 ? _a : GRID_CONSTANTS.DEFAULT_SIZE;
            const roundedSize = Math.round(currentSize);
            onLocalGridChange({
                enabled: (_b = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.enabled) !== null && _b !== void 0 ? _b : false,
                size: roundedSize,
                color: (_c = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.color) !== null && _c !== void 0 ? _c : GRID_CONSTANTS.DEFAULT_COLOR,
            });
        }
    }, [onLocalGridChange, currentGrid]);
    const handleColorChange = useCallback((color) => {
        var _a, _b;
        setLocalColor(color);
        debouncedOnLocalGridChange({
            enabled: (_a = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.enabled) !== null && _a !== void 0 ? _a : false,
            size: (_b = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.size) !== null && _b !== void 0 ? _b : GRID_CONSTANTS.DEFAULT_SIZE,
            color,
        });
    }, [debouncedOnLocalGridChange, currentGrid]);
    const enabled = (_b = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.enabled) !== null && _b !== void 0 ? _b : false;
    return (_jsxs("div", { style: {
            background: enabled ? '#1D2225' : '#2A2D2ECD',
            borderRadius: 10,
            padding: 0,
            paddingRight: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
        }, children: [_jsx(Tooltip, { text: "\u0421\u0435\u0442\u043A\u0430 \u043A\u0430\u0440\u0442\u044B", position: "top", children: _jsx("button", { type: "button", onClick: () => handleEnabledChange(!enabled), style: tileButton(enabled, { boxShadow: 'none' }), "aria-label": "\u0421\u0435\u0442\u043A\u0430 \u043A\u0430\u0440\u0442\u044B", children: _jsx(Icon, { name: "grid_local_1" }) }) }), _jsxs("div", { style: {
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    opacity: enabled ? 1 : 0.2,
                }, children: [_jsx(NumberInput, { value: (_c = currentGrid === null || currentGrid === void 0 ? void 0 : currentGrid.size) !== null && _c !== void 0 ? _c : GRID_CONSTANTS.DEFAULT_SIZE, min: GRID_CONSTANTS.MIN_SIZE, max: GRID_CONSTANTS.MAX_SIZE, step: fixedStep ? Number(STEP_MODES.FIXED) : Number(STEP_MODES.FLOATING_1), onChange: handleSizeChange, disabled: !enabled, width: 70 }), _jsx(Tooltip, { text: "\u0422\u043E\u0447\u043D\u043E\u0441\u0442\u044C \u0441\u0435\u0442\u043A\u0438", position: "top", children: _jsx("button", { type: "button", onClick: () => handleFixedStepChange(!fixedStep), style: tileButton(!fixedStep, { boxShadow: 'none', padding: 0, height: 30 }), "aria-label": "\u0422\u043E\u0447\u043D\u043E\u0441\u0442\u044C \u0441\u0435\u0442\u043A\u0438", children: _jsx("span", { style: {
                                    fontSize: 12,
                                    fontWeight: 'bold',
                                    fontFamily: 'monospace',
                                    fontStyle: 'italic',
                                    color: '#cccccc',
                                    opacity: 0.5,
                                }, children: ".00" }) }) }), _jsx(Tooltip, { text: "\u0426\u0432\u0435\u0442 \u0441\u0435\u0442\u043A\u0438", position: "top", children: _jsx("input", { type: "color", value: localColor, onChange: (e) => handleColorChange(e.target.value), disabled: !enabled, style: {
                                width: 32,
                                height: 32,
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                background: 'transparent',
                                cursor: enabled ? 'pointer' : 'not-allowed',
                            } }) })] })] }));
};
